// Baixa num ZIP só os PDFs do mês (guia, extrato...) das empresas marcadas.
// O backend marca quem entrou no ZIP como "Já baixados em lote".
import React, { useState } from 'react';
import {
  Button,
  Checkbox,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { TIPOS_DOCUMENTO, TipoDocumento, mensagemErro, snApi } from './api';

function salvarArquivo(arquivo: Blob, nome: string) {
  const url = URL.createObjectURL(arquivo);
  const a = document.createElement('a');
  a.href = url;
  a.download = nome;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

export function BaixarDocumentosModal({
  competencia,
  empresaIds,
  onFechar,
}: {
  competencia: string;
  /** vazio = todas as empresas do mês */
  empresaIds: number[];
  onFechar: () => void;
}) {
  const qc = useQueryClient();
  const [tipos, setTipos] = useState<Set<TipoDocumento>>(
    new Set(TIPOS_DOCUMENTO.filter(t => t.padrao).map(t => t.tipo)),
  );
  const [soNaoBaixados, setSoNaoBaixados] = useState(true);

  const baixar = useMutation({
    mutationFn: () =>
      snApi.baixarDocumentos({
        competencia,
        empresa_ids: empresaIds,
        tipos: Array.from(tipos),
        so_nao_baixados: soNaoBaixados,
      }),
    onSuccess: r => {
      salvarArquivo(r.arquivo, r.nome);
      const [com, total] = (r.empresas ?? '').split('/');
      toast.success(
        r.empresas
          ? `${com} de ${total} empresa(s) no ZIP. As sem documento estão em _FALTANDO.txt.`
          : 'ZIP baixado.',
      );
      qc.invalidateQueries({ queryKey: ['sn-lista'] });
      qc.invalidateQueries({ queryKey: ['sn-totais'] });
      qc.invalidateQueries({ queryKey: ['sn-detalhe'] });
      onFechar();
    },
    onError: e => toast.error(mensagemErro(e)),
  });

  const alternar = (t: TipoDocumento) =>
    setTipos(prev => {
      const n = new Set(prev);
      if (n.has(t)) n.delete(t);
      else n.add(t);
      return n;
    });

  return (
    <Modal isOpen onClose={onFechar} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="16px">Baixar documentos (ZIP)</ModalHeader>
        <ModalCloseButton />
        <ModalBody fontSize="13px">
          <Text mb={3} color="#475569">
            {empresaIds.length
              ? `${empresaIds.length} empresa(s) marcada(s).`
              : 'Todas as empresas do mês.'}{' '}
            Todos os PDFs soltos no ZIP, sem subpastas, com o nome &quot;código - empresa - DAS
            10-2026.pdf&quot;. Quem ainda não tem o documento gerado vai listado no _FALTANDO.txt.
          </Text>
          <Text fontWeight={600} mb={1}>
            Documentos
          </Text>
          <Flex direction="column" gap={1} mb={4}>
            {TIPOS_DOCUMENTO.map(t => (
              <Checkbox
                key={t.tipo}
                isChecked={tipos.has(t.tipo)}
                onChange={() => alternar(t.tipo)}
              >
                {t.rotulo}
              </Checkbox>
            ))}
          </Flex>
          <Checkbox isChecked={soNaoBaixados} onChange={e => setSoNaoBaixados(e.target.checked)}>
            Só as que ainda não baixei
          </Checkbox>
          <Text fontSize="11px" color="#64748B" mt={1} pl={6}>
            Desmarcado, baixa de novo também as que já estão em &quot;Já baixados em lote&quot;.
          </Text>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button size="sm" variant="ghost" onClick={onFechar}>
            Cancelar
          </Button>
          <Button
            size="sm"
            colorScheme="blue"
            isDisabled={tipos.size === 0}
            isLoading={baixar.isPending}
            loadingText="Montando ZIP…"
            onClick={() => baixar.mutate()}
          >
            Baixar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
